import { useState, useMemo, useEffect } from 'react';
import { X, ArrowUpDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import FormattedDate from '@/components/common/FormattedDate';
import { useWorkflowTemplates, useCopyTemplateVariables } from '@/hooks/useWorkflows';
import type { WorkflowTemplate } from '@/lib/types';

interface Props {
  destTemplate: WorkflowTemplate;
  onClose: () => void;
}

type SortField = 'type_name' | 'subtype' | 'version' | 'environment' | 'variables' | 'create_ts';
type SortDir = 'asc' | 'desc';

// Module scope so it keeps a stable identity across renders; sort state is passed in via props.
function SortHeader({
  field,
  children,
  sortField,
  sortDir,
  onSort,
}: {
  field: SortField;
  children: React.ReactNode;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) {
  return (
    <th
      className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none"
      onClick={() => onSort(field)}
      aria-sort={sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={cn('h-3 w-3', sortField === field ? 'text-primary' : 'opacity-40')} />
      </span>
    </th>
  );
}

const SORT_COLUMNS: readonly (readonly [SortField, string])[] = [
  ['type_name', 'Type'],
  ['subtype', 'Subtype'],
  ['version', 'Version'],
  ['environment', 'Environment'],
  ['variables', 'Variables'],
  ['create_ts', 'Created'],
];

export default function CopyVariablesModal({ destTemplate, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const { data: templatesData, isLoading } = useWorkflowTemplates({ limit: 500 });
  const copyMutation = useCopyTemplateVariables();

  const templates = useMemo(() => {
    if (!templatesData?.items) return [];

    // Exclude destination template
    let filtered = templatesData.items.filter((t) => t.id !== destTemplate.id);

    // Client-side search
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.type_name.toLowerCase().includes(q) ||
          t.subtype.toLowerCase().includes(q) ||
          t.version.toLowerCase().includes(q) ||
          (t.description?.toLowerCase().includes(q) ?? false)
      );
    }

    // Sort
    if (sortField) {
      filtered.sort((a, b) => {
        let cmp = 0;
        if (sortField === 'variables') {
          cmp = (a.variables?.length ?? 0) - (b.variables?.length ?? 0);
        } else if (sortField === 'create_ts') {
          cmp = a.create_ts.localeCompare(b.create_ts);
        } else {
          cmp = (a[sortField] ?? '').localeCompare(b[sortField] ?? '');
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else {
      // Default: same type_name first, then others; within each group by create_ts asc
      filtered.sort((a, b) => {
        const aMatch = a.type_name === destTemplate.type_name ? 0 : 1;
        const bMatch = b.type_name === destTemplate.type_name ? 0 : 1;
        if (aMatch !== bMatch) return aMatch - bMatch;
        return a.create_ts.localeCompare(b.create_ts);
      });
    }

    return filtered;
  }, [templatesData, destTemplate, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSelect = (source: WorkflowTemplate) => {
    if (!window.confirm('This will overwrite configuration for variables with matching names. Continue?')) return;

    copyMutation.mutate(
      { destTemplateId: destTemplate.id, sourceTemplateId: source.id },
      {
        onSuccess: () => {
          toast.success('Variables copied successfully');
          onClose();
        },
        onError: (err: Error) => {
          toast.error(`Failed to copy variables: ${err.message}`);
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold">Copy Variables From...</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-border">
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">No templates found</div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  {SORT_COLUMNS.map(([field, label]) => (
                    <SortHeader
                      key={field}
                      field={field}
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    >
                      {label}
                    </SortHeader>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {templates.map((t) => (
                  <tr
                    key={t.id}
                    role="button"
                    tabIndex={copyMutation.isPending ? -1 : 0}
                    onClick={() => handleSelect(t)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !copyMutation.isPending) {
                        e.preventDefault();
                        handleSelect(t);
                      }
                    }}
                    className={cn(
                      'cursor-pointer hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50',
                      copyMutation.isPending && 'pointer-events-none opacity-50'
                    )}
                  >
                    <td className="px-3 py-2 text-sm font-medium">{t.type_name}</td>
                    <td className="px-3 py-2 text-sm">{t.subtype}</td>
                    <td className="px-3 py-2 text-sm">{t.version}</td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          t.environment === 'production' ? 'bg-green-500/10 text-green-500'
                            : t.environment === 'staging' ? 'bg-yellow-500/10 text-yellow-500'
                            : t.environment === 'development' ? 'bg-blue-500/10 text-blue-500'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {t.environment}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-center">{t.variables?.length ?? 0}</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">
                      <FormattedDate date={t.create_ts} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-border hover:bg-muted transition-colors text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
